<template>
  <div data-locator="share-content">
    <v-row class="text-text-secondary">
      <v-col>
        <template v-if="singleFile">
          {{ $t('common.document-share.DocumentShare.infoSingleFile') }}
        </template>
        <template v-if="singleFolder">
          <p>{{ $t('common.document-share.DocumentShare.infoSingleFolder') }}</p>
          <p>{{ $t('common.document-share.DocumentShare.infoSingleFile') }}</p>
        </template>
        <template v-if="multipleDocuments">
          <p>{{ $t('common.document-share.DocumentShare.infoSingleFolder') }}</p>
          <p>{{ $t('common.document-share.DocumentShare.infoSingleFile') }}</p>
        </template>
      </v-col>
    </v-row>
    <v-row class="mt-0">
      <v-col>
        <app-tile-group
          :model-value="shareType"
          :options="shareTypeOptions"
          @update:model-value="onShareTypeChange"
        />
      </v-col>
    </v-row>
    <v-row v-if="shareType === SHARE_TYPE_OPTIONS.CUSTOM && groups"
           class="mt-4"
    >
      <v-col v-if="documentsTablePane.isOpen">
        <MembersAutocomplete :items="allRoomMembers" @select="onMemberSelect" />
        <DocumentSharePicker :force-pm-selection="forcePmSelection" />
      </v-col>
    </v-row>
    <v-divider class="my-4" />
    <v-row v-if="singleFile && documents[0].nbVersions > 1">
      <v-col>
        <v-checkbox v-model="applyToOldVersions"
                    class="text-text-secondary"
                    hide-details
                    :label="$t('common.document-share.DocumentShare.applyToOldVersions')"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <app-toggle
          v-model="sendNotif"
          hide-details
          :label="$t('common.document-share.DocumentShare.sendNotif')"
        />
      </v-col>
    </v-row>
    <v-row v-if="sendNotif">
      <v-col>
        <VisualTextEditor
          v-model:value="notifyMessage"
          ref="notifyMessageEditor"
          counter="footer"
          :error-messages="notifyMessageErrors"
          :max-length="notifyMessageMaxLength"
          :placeholder="$t('common.document-share.DocumentShare.optionalMessageWithCopy')"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex'

import { SHARE_TYPE_OPTIONS } from '@/common/utils/shareTypes'
import MembersAutocomplete from '@/project/members/MembersAutocomplete.vue'
import { DocumentSharePickerActionTypes } from '@/store/modules/document-share-picker/action_types'
import { DocumentSharePickerMutationTypes } from '@/store/modules/document-share-picker/mutation_types'
import { SHARE_FILDERS } from '@/store/modules/documents/action_types'

import DocumentSharePicker from './DocumentSharePicker.vue'
import { escapeHtml } from '../utils/strings'
import { toast } from '../utils/toast'
import VisualTextEditor from '../visual-text-editor/VisualTextEditor.vue'

const NOTIFY_MESSAGE_MAX_LENGTH = 400

export default {
  name: 'DocumentShare',
  components: { MembersAutocomplete, VisualTextEditor, DocumentSharePicker },
  emits: ['update:submit-disabled'],
  props: {
    documents: {
      type: Array,
      required: true,
    },
    forcePmSelection: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      SHARE_TYPE_OPTIONS,
      shareTypeValue: '',
      sendNotif: true,
      applyToOldVersions: false,
      selectedGroupsAndUsers: [],
      notifyMessage: '',
      notifyMessageMaxLength: NOTIFY_MESSAGE_MAX_LENGTH,
    }
  },
  computed: {
    ...mapGetters('groups', ['allRoomMembers']),
    ...mapState('groups', ['groups']),
    ...mapState('documents', ['shareLoading', 'documentsTablePane']),
    singleFile () {
      return this.documents.length === 1 && this.documents[0].type === 'file'
    },
    singleFolder () {
      return this.documents.length === 1 && this.documents[0].type === 'folder'
    },
    multipleDocuments () {
      return this.documents.length > 1
    },
    shareType () {
      if (this.shareTypeValue) {
        return this.shareTypeValue
      } else if (this.documents[0]?.rights) {
        return this.documents[0].rights.read.isPublic
          ? SHARE_TYPE_OPTIONS.PUBLIC
          : SHARE_TYPE_OPTIONS.CUSTOM
      }
      return SHARE_TYPE_OPTIONS.CUSTOM
    },
    shareTypeOptions () {
      return [
        {
          label: this.$t('common.document-share.DocumentShare.publicTitle'),
          value: SHARE_TYPE_OPTIONS.PUBLIC,
          dataLocator: `${SHARE_TYPE_OPTIONS.PUBLIC}-share-option`,
        },
        {
          label: this.$t('common.document-share.DocumentShare.customTitle'),
          value: SHARE_TYPE_OPTIONS.CUSTOM,
          dataLocator: `${SHARE_TYPE_OPTIONS.CUSTOM}-share-option`,
        },
      ]
    },
    notifyMessageLength () {
      return escapeHtml(this.notifyMessage).length
    },
    notifyMessageIsTooLong () {
      return this.notifyMessageLength > this.notifyMessageMaxLength
    },
    notifyMessageErrors () {
      const errors = []

      if (this.notifyMessageIsTooLong) {
        errors.push(this.$t('common.validations.textTooLong'))
      }

      return errors
    },
    shareDisabled () {
      return this.notifyMessageIsTooLong
    },
  },
  watch: {
    // The confirm action now sits in the drawer footer, outside of the scrollable body, so the
    // pane that renders it has to be told when this form becomes submittable.
    shareDisabled: {
      handler (disabled) {
        this.$emit('update:submit-disabled', disabled)
      },
      immediate: true,
    },
    'documentsTablePane.isOpen' (value) {
      if (!value) {
        this.shareTypeValue = ''
        this.$refs.notifyMessageEditor?.clear()
      }
    },
  },
  methods: {
    ...mapActions('documents', [SHARE_FILDERS]),
    ...mapActions('documentSharePicker', [
      DocumentSharePickerActionTypes.HIGHLIGHT_MEMBER,
      DocumentSharePickerActionTypes.INIT_SELECTED_GROUPS,
      DocumentSharePickerActionTypes.INIT_SELECTED_MEMBERS,
    ]),
    ...mapMutations('documentSharePicker', [
      DocumentSharePickerMutationTypes.ADD_MEMBERS_TO_SELECTED,
    ]),
    async share () {
      try {
        await this.SHARE_FILDERS({
          shareType: this.shareType,
          sendNotif: this.sendNotif,
          notifyMessage: this.notifyMessage,
          applyToOldVersions: this.applyToOldVersions,
        })
        toast.success(this.$t('common.document-share.DocumentShare.shareSuccess'))
      } catch (error) {
        console.error('Erreur lors du partage:', error)
        toast.error(this.$t('common.document-share.DocumentShare.shareError'))
      }
    },
    onShareTypeChange(value) {
      this.shareTypeValue = value
      if (value === SHARE_TYPE_OPTIONS.CUSTOM) {
        this.INIT_SELECTED_GROUPS(this.documents[0].rights.read.groups)
        this.INIT_SELECTED_MEMBERS(this.documents[0].rights.read.users)
      }
    },
    onMemberSelect (member) {
      if (member) {
        this.ADD_MEMBERS_TO_SELECTED([member])
        this.HIGHLIGHT_MEMBER(member)
      }
    },
  },
}
</script>

<style scoped lang="scss">
.DocumentShare-radio {
  // Deliberate exception: 7px selection band has no matching border-width token (only 0/1/2px
  // exist) and rounding to 2px would visibly thin it without design validation (AB#3108019).
  border-left: 7px solid transparent;
  margin-left: -20px;
  margin-right: -20px;
  padding-top: 14px;
  padding-bottom: 14px;

  &.v-selection-control--dirty {
    border-left-color: rgb(var(--v-theme-primary));
    background-color: rgb(var(--v-theme-primary-lighten-4));
  }
}
:deep(.v-selection-control__input .v-icon) {
  font-size: 18px;
}
:deep(.v-selection-control-group) {
  width: 100%;
}
</style>
